import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useDialog } from '../components/AppDialog';
import { usePageSEO } from '../hooks/usePageSEO';
import ResumePreview from '../components/ResumePreview';
import { TEMPLATES, CV_TEMPLATES, CL_TEMPLATES } from '../data/samples';
import { startCheckout } from '../utils/stripe';
import { MarketingHeader, MarketingFooter, LandingStyles, buildShowcaseResume } from './OtherPages';
import {
  ArrowRight, Sparkles, Zap, CheckCircle2, Check, X, Star, Users, Award, Layers, Clock,
  Target, Wand2, Video, Briefcase, Linkedin, TrendingUp,
  ChevronLeft, ChevronRight, ShieldCheck, RefreshCw, Quote,
  DollarSign, LayoutGrid, FileText, Bot, Mail, Mic, Rocket,
} from 'lucide-react';

// ── Data ───────────────────────────────────────────────────────────────────────
const MONTHLY = 19.99;
const ANNUAL = 160;
const ANNUAL_PER_MONTH = (ANNUAL / 12).toFixed(2);
const SAVE_PCT = Math.round((1 - ANNUAL / (MONTHLY * 12)) * 100);

const FREE_FEATURES = ['3 saved documents', 'All templates', 'Resume, cover letter & CV builder', 'Limited AI generation', 'PDF export', 'Live preview editor'];
const PRO_FEATURES = ['Unlimited documents', 'Unlimited AI generation', 'ATS job tailoring', 'AI bullet point generator', 'AI interview coach', 'Priority support'];

const TRUST_STATS = [
  { Icon: Users, value: 500, suffix: '+', label: 'Resumes built' },
  { Icon: Award, value: 100, suffix: '+', label: 'Professionals trust us' },
  { Icon: Star, value: 4.9, decimals: 1, suffix: '★', label: 'Average rating' },
  { Icon: Layers, value: TEMPLATES.length + CV_TEMPLATES.length + CL_TEMPLATES.length, suffix: '+', label: 'Premium templates' },
];

const COMPARISON_ROWS = [
  ['Generic templates recruiters have seen a thousand times', 'Modern, recruiter-approved designs that stand out'],
  ['Hours spent formatting and re-formatting in Word', 'A polished resume ready in minutes'],
  ['No idea if your resume passes ATS scanners', 'Instant ATS score with specific fixes'],
  ['The same resume sent to every job', 'AI-tailored resume for each application'],
  ['Silence after hitting submit', 'More interview callbacks'],
  ['Winging it in interviews', 'AI interview coach to practice and improve'],
];

const WORKFLOW_STEPS = [
  { Icon: Bot, title: 'AI Resume Builder', desc: 'Create a professional ATS-friendly resume in minutes using AI.' },
  { Icon: Target, title: 'ATS Resume Score', desc: 'Analyze your resume and instantly discover what recruiters see.' },
  { Icon: Wand2, title: 'AI Job Tailoring', desc: 'Customize your resume for every job description with one click.' },
  { Icon: Mail, title: 'Cover Letter Generator', desc: 'Generate personalized cover letters that match your resume and job.' },
  { Icon: Linkedin, title: 'LinkedIn Optimizer', desc: 'Improve your LinkedIn profile to attract recruiters.' },
  { Icon: Mic, title: 'AI Interview Practice', desc: 'Practice real interview questions with AI feedback and scoring.' },
  { Icon: LayoutGrid, title: 'Job Tracker', desc: 'Track applications, interviews, offers, and follow-ups in one dashboard.' },
  { Icon: Rocket, title: 'Get Hired', desc: 'Apply with confidence and increase your chances of landing interviews.' },
];

const TESTIMONIALS = [
  { quote: 'Upgrading to Pro paid for itself the moment I got my first interview request — worth every penny.', name: 'Jordan M.', role: 'Data Analyst', initials: 'JM', color: '#EF1D28' },
  { quote: 'I was skeptical about paying for a resume tool, but the ATS tailoring got me past filters I’d been stuck behind for months.', name: 'Priya R.', role: 'Marketing Specialist', initials: 'PR', color: '#7C3AED' },
  { quote: 'Free plan got me started, Pro got me hired. Three interviews in my first two weeks.', name: 'Carlos D.', role: 'Software Engineer', initials: 'CD', color: '#059669' },
  { quote: 'The AI interview coach was the real unlock for me — I walked into my final round prepared instead of anxious.', name: 'Aisha K.', role: 'Product Manager', initials: 'AK', color: '#0891B2' },
];

const FAQS = [
  ['Can I start for free?', 'Yes. Sign up with your email or Google/LinkedIn account — no credit card required. The free plan gives you immediate access to the document builder, all templates, AI writing tools, ATS Checker, Job Search, and Interview Prep.'],
  ['Do I need a credit card to sign up?', 'Never. Creating a free account and using all core features requires no payment information at any stage. You only need to enter card details if you choose to upgrade to Pro.'],
  ['What does the free plan include?', 'The free plan includes the resume, CV, and cover letter builder; all templates; AI content generation; ATS score checking; PDF export; Job Search; Interview Prep; and the Refer & Earn program. There is a 3-document limit on the free plan.'],
  ['What does Pro add?', 'Pro removes the document limit so you can maintain a full library of tailored resumes, unlocks unlimited AI processing, adds ATS tailoring rewrites, an AI interview coach, and priority support.'],
  ['What is the difference between monthly and yearly?', `Monthly is $${MONTHLY}/month billed each month. Yearly is $${ANNUAL}/year (just $${ANNUAL_PER_MONTH}/month) — you save ${SAVE_PCT}% compared to monthly. Both give identical features.`],
  ['Can I cancel at any time?', 'Yes. No lock-in on either plan. Cancel from your account settings and your Pro access continues until the end of the billing period. Your documents are never deleted.'],
  ['Which payment methods do you accept?', 'All major credit and debit cards via Stripe, our secure payment processor. We never store your card details on our servers.'],
  ['Is there a refund policy?', 'If Pro isn’t right for you, contact support within 7 days of upgrading and we’ll issue a full refund — no questions asked.'],
];

// ── Small shared pieces ───────────────────────────────────────────────────────
const AnimatedCounter = ({ value, decimals = 0, duration = 1600, suffix = '', prefix = '' }) => {
  const ref = useRef(null);
  const startedRef = useRef(false);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !startedRef.current) {
        startedRef.current = true;
        const start = performance.now();
        const step = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setDisplay(value * eased);
          if (progress < 1) requestAnimationFrame(step);
          else setDisplay(value);
        };
        requestAnimationFrame(step);
        observer.disconnect();
      }
    }, { threshold: 0.4 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [value, duration]);

  return <span ref={ref}>{prefix}{display.toFixed(decimals)}{suffix}</span>;
};

const StarRow = ({ n = 5, size = 13 }) => (
  <div style={{ display: 'flex', gap: 2 }}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={size} fill={i < n ? '#FBBF24' : 'none'} color={i < n ? '#FBBF24' : '#D1D5DB'} strokeWidth={1.5} />
    ))}
  </div>
);

const SectionHeading = ({ title, subtitle }) => (
  <div className="prc-sec-heading">
    <h2>{title}</h2>
    {subtitle && <p>{subtitle}</p>}
  </div>
);

// ── Hero ───────────────────────────────────────────────────────────────────────
const DASH_ROWS = [
  { Icon: FileText, label: 'Resume Score', sub: 'Optimized & ready', value: '94%', color: '#059669', bg: '#ECFDF5' },
  { Icon: Target, label: 'ATS Score', sub: 'Passes major scanners', value: '91%', color: '#1D4ED8', bg: '#EFF6FF' },
  { Icon: Video, label: 'AI Interview', sub: 'Mock round completed', value: 'Ready', color: '#7C3AED', bg: '#F5F3FF' },
  { Icon: Briefcase, label: 'Job Tracker', sub: 'This month', value: '12 apps', color: '#D97706', bg: '#FFFBEB' },
  { Icon: Sparkles, label: 'Resume Builder', sub: 'Active drafts', value: '3 docs', color: '#EF1D28', bg: '#FFF1F2' },
];

const HeroSection = ({ user, navigate, onSeePricing }) => (
  <section className="prc-hero">
    <div className="prc-hero-grid-bg" />
    <div className="prc-hero-glow" />
    <div className="prc-hero-inner">
      <div className="prc-hero-left">
        <h1>One upgrade away from<br />your next interview.</h1>
        <p>Start free and build a job-ready resume in minutes. Upgrade to Pro when you're ready for unlimited AI tailoring, ATS optimization, and interview prep.</p>
        <div className="prc-hero-ctas">
          {user ? (
            <button type="button" className="prc-btn-primary" onClick={() => navigate('/dashboard')}>Go to Dashboard <ArrowRight size={16} /></button>
          ) : (
            <button type="button" className="prc-btn-primary" onClick={() => navigate('/build/resume')}><Sparkles size={16} /> Start Building Free</button>
          )}
          <button type="button" className="prc-btn-ghost-dark" onClick={onSeePricing}>View Plans</button>
        </div>
      </div>
      <div className="prc-hero-right">
        <div className="prc-dash-wrap">
          <motion.div className="prc-dash-card" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="prc-dash-chrome">
              <span /><span /><span />
              <div className="prc-dash-url"><span>🔒</span> marsresume.com/dashboard</div>
            </div>
            <div className="prc-dash-body">
              {DASH_ROWS.map(row => (
                <div key={row.label} className="prc-dash-row">
                  <div className="prc-dash-row-icon" style={{ background: row.bg, color: row.color }}><row.Icon size={16} /></div>
                  <div className="prc-dash-row-text">
                    <div className="prc-dash-row-label">{row.label}</div>
                    <div className="prc-dash-row-sub">{row.sub}</div>
                  </div>
                  <div className="prc-dash-row-value" style={{ color: row.color, background: row.bg }}>{row.value}</div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div className="prc-float-badge prc-float-badge-1" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.5 }}>
            <span className="prc-float-icon prc-float-icon-green"><CheckCircle2 size={15} /></span>
            <span>Resume Score: 94%</span>
          </motion.div>
          <motion.div className="prc-float-badge prc-float-badge-2" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.7 }}>
            <span className="prc-float-icon prc-float-icon-purple"><Video size={15} /></span>
            <span>AI Interview Ready</span>
          </motion.div>
        </div>
      </div>
    </div>
  </section>
);

// ── Trust stats ──────────────────────────────────────────────────────────────
const TrustStatsSection = () => (
  <section className="prc-section">
    <motion.div className="prc-stats-grid" initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }}
      variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}>
      {TRUST_STATS.map(stat => (
        <motion.div key={stat.label} className="prc-stat-card" variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }} transition={{ duration: 0.4, ease: 'easeOut' }}
          whileHover={{ y: -4 }}>
          <div className="prc-stat-icon"><stat.Icon size={20} /></div>
          <div className="prc-stat-value"><AnimatedCounter value={stat.value} decimals={stat.decimals || 0} suffix={stat.suffix} /></div>
          <div className="prc-stat-label">{stat.label}</div>
        </motion.div>
      ))}
    </motion.div>
  </section>
);

// ── Pricing toggle + cards ───────────────────────────────────────────────────
const PricingToggle = ({ billingCycle, setBillingCycle }) => (
  <div className="prc-toggle">
    {['monthly', 'annual'].map(cycle => {
      const isActive = billingCycle === cycle;
      return (
        <button key={cycle} type="button" className={`prc-toggle-btn${isActive ? ' active' : ''}`} onClick={() => setBillingCycle(cycle)}>
          {isActive && <motion.span layoutId="prcToggleBg" className="prc-toggle-bg" transition={{ type: 'spring', stiffness: 420, damping: 34 }} />}
          <span className="prc-toggle-content">
            {cycle === 'monthly' ? 'Monthly' : 'Yearly'}
            {cycle === 'annual' && <span className="prc-toggle-save">Save {SAVE_PCT}%</span>}
          </span>
        </button>
      );
    })}
  </div>
);

const PricingCardsSection = ({ user, isFreeUser, isPro, billingCycle, setBillingCycle, goToBuilder, handleUpgrade }) => (
  <section className="prc-section" id="prc-pricing">
    <SectionHeading title="Simple, transparent pricing" subtitle="Start free. Upgrade when you're ready to go all-in on your job search." />
    <div className="prc-toggle-wrap"><PricingToggle billingCycle={billingCycle} setBillingCycle={setBillingCycle} /></div>

    <div className="prc-cards-grid">
      {/* Free */}
      <motion.div className="prc-card" whileHover={{ y: -6 }} transition={{ type: 'spring', stiffness: 300, damping: 24 }}>
        <div className="prc-card-badge prc-card-badge-neutral">FREE FOREVER</div>
        <div className="prc-card-name">Free</div>
        <div className="prc-card-desc">Perfect for getting started with your job search</div>
        <div className="prc-card-price">
          <span className="prc-card-price-amount">$0</span>
          <span className="prc-card-price-period">/month</span>
        </div>
        <div className="prc-card-price-sub">Free forever, no card needed</div>
        {isFreeUser
          ? <div className="prc-card-current">✓ Your Current Plan</div>
          : <button type="button" className="prc-btn-dark-full" onClick={goToBuilder}>Get Started Free <ArrowRight size={16} /></button>}
        <div className="prc-card-divider" />
        <div className="prc-card-features">
          {FREE_FEATURES.map(f => (
            <div key={f} className="prc-card-feature">
              <span className="prc-card-feature-icon prc-card-feature-icon-neutral"><CheckCircle2 size={13} strokeWidth={2.5} /></span>
              {f}
            </div>
          ))}
        </div>
      </motion.div>

      {/* Pro */}
      <motion.div className="prc-card prc-card-pro" whileHover={{ y: -6 }} transition={{ type: 'spring', stiffness: 300, damping: 24 }}>
        <div className="prc-card-ribbon">Most Popular</div>
        <div className="prc-card-badge prc-card-badge-pro">PRO PLAN</div>
        <div className="prc-card-name">Pro</div>
        <div className="prc-card-desc">Everything you need to land your dream job</div>
        <AnimatePresence mode="wait">
          <motion.div key={billingCycle} className="prc-card-price" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}>
            <span className="prc-card-price-amount">${billingCycle === 'annual' ? ANNUAL_PER_MONTH : MONTHLY}</span>
            <span className="prc-card-price-period">/month</span>
          </motion.div>
        </AnimatePresence>
        <div className="prc-card-price-sub">{billingCycle === 'annual' ? `Billed annually at $${ANNUAL}` : 'Billed monthly · Cancel anytime'}</div>
        {isPro
          ? <div className="prc-card-current prc-card-current-pro">✓ Your Current Plan</div>
          : <button type="button" className="prc-btn-white-full" onClick={() => handleUpgrade(billingCycle)}>Upgrade to Pro <ArrowRight size={16} /></button>}
        <div className="prc-card-divider prc-card-divider-pro" />
        <div className="prc-card-features">
          {PRO_FEATURES.map(f => (
            <div key={f} className="prc-card-feature prc-card-feature-pro">
              <span className="prc-card-feature-icon prc-card-feature-icon-pro"><CheckCircle2 size={13} strokeWidth={2.5} /></span>
              {f}
            </div>
          ))}
        </div>
      </motion.div>
    </div>

    <div className="prc-trust-row">
      {[
        { Icon: ShieldCheck, text: 'Secure payments via Stripe' },
        { Icon: RefreshCw, text: 'Cancel anytime, no questions' },
        { Icon: Zap, text: 'Instant access after upgrade' },
      ].map(({ Icon, text }) => (
        <div key={text} className="prc-trust-item"><Icon size={14} /> {text}</div>
      ))}
    </div>
  </section>
);

// ── Outcome comparison ───────────────────────────────────────────────────────
const ComparisonSection = () => (
  <section className="prc-section prc-section-tint">
    <SectionHeading title="Without MarsResume vs. with MarsResume" subtitle="The difference isn't features — it's outcomes." />
    <div className="prc-compare-grid">
      <div className="prc-compare-col prc-compare-col-without">
        <div className="prc-compare-head prc-compare-head-without">Without MarsResume</div>
        {COMPARISON_ROWS.map(([without]) => (
          <div key={without} className="prc-compare-row">
            <span className="prc-compare-icon prc-compare-icon-without"><X size={13} strokeWidth={3} /></span>
            {without}
          </div>
        ))}
      </div>
      <div className="prc-compare-col prc-compare-col-with">
        <div className="prc-compare-head prc-compare-head-with">With MarsResume</div>
        {COMPARISON_ROWS.map(([, withMr]) => (
          <div key={withMr} className="prc-compare-row">
            <span className="prc-compare-icon prc-compare-icon-with"><Check size={13} strokeWidth={3} /></span>
            {withMr}
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ── Workflow ─────────────────────────────────────────────────────────────────
const WorkflowSection = ({ user, navigate }) => {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 900);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <section className="prc-section prc-workflow-outer">
      <SectionHeading title="Everything You Need to Land Your Next Job" subtitle="MarsResume isn't just a resume builder. It's your complete AI career platform—from creating your resume to landing interviews." />

      <motion.div className="prc-workflow-grid" initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.1 }}
        variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}>
        {WORKFLOW_STEPS.map((step, i) => {
          const isRowBreak = i % 4 === 3;
          return (
            <React.Fragment key={step.title}>
              <motion.div className="prc-workflow-card" variants={{ hidden: { opacity: 0, y: 30 }, show: { opacity: 1, y: 0 } }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} whileHover={{ y: -8 }}>
                <div className="prc-workflow-step-num">{String(i + 1).padStart(2, '0')}</div>
                <motion.div className="prc-workflow-icon" animate={{ y: [0, -5, 0] }} transition={{ duration: 3 + (i % 3), repeat: Infinity, ease: 'easeInOut', delay: i * 0.15 }}>
                  <step.Icon size={22} />
                </motion.div>
                <div className="prc-workflow-title">{step.title}</div>
                <p className="prc-workflow-desc">{step.desc}</p>
              </motion.div>
              {i < WORKFLOW_STEPS.length - 1 && (
                <div className={`prc-workflow-arrow${isRowBreak ? ' prc-workflow-arrow-wrap' : ''}`}>
                  <motion.div className="prc-workflow-arrow-line" style={{ transformOrigin: isMobile ? 'top' : 'left' }}
                    initial={isMobile ? { scaleY: 0 } : { scaleX: 0 }} whileInView={isMobile ? { scaleY: 1 } : { scaleX: 1 }}
                    viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.15 + i * 0.06 }} />
                  <div className="prc-workflow-arrow-chip"><ArrowRight size={14} /></div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </motion.div>

      <motion.div className="prc-workflow-summary" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.5 }}>
        <div className="prc-workflow-summary-emoji">✨</div>
        <div className="prc-workflow-summary-text">
          <strong>One subscription.</strong>
          <span>Everything you need to create, optimize, tailor, practice, track, and land your next job.</span>
        </div>
        <button type="button" className="prc-workflow-cta" onClick={() => navigate(user ? '/dashboard' : '/build/resume')}>
          Start Building Free <ArrowRight size={16} />
        </button>
        <div className="prc-workflow-summary-sub">No credit card required.</div>
      </motion.div>
    </section>
  );
};

// ── ROI calculator ───────────────────────────────────────────────────────────
const IMPROVEMENT_MULTIPLIER = 1.4;
const ROICalculatorSection = () => {
  const [apps, setApps] = useState(20);
  const [rate, setRate] = useState(5);

  const results = useMemo(() => {
    const currentInterviewsYear = apps * (rate / 100) * 12;
    const improvedInterviewsYear = currentInterviewsYear * IMPROVEMENT_MULTIPLIER;
    const additionalInterviews = Math.max(0, Math.round(improvedInterviewsYear - currentInterviewsYear));
    const timeSavedHoursPerMonth = Math.round((apps * 17 / 60) * 10) / 10;
    const salaryPct = Math.min(20, Math.round(5 + additionalInterviews * 0.3));
    return { additionalInterviews, timeSavedHoursPerMonth, salaryPct };
  }, [apps, rate]);

  return (
    <section className="prc-section prc-section-tint">
      <SectionHeading title="See what MarsResume could be worth to you" subtitle="Adjust the numbers to match your job search." />
      <div className="prc-roi-card">
        <div className="prc-roi-inputs">
          <div className="prc-roi-input">
            <div className="prc-roi-input-label">
              <span>Applications per month</span>
              <span className="prc-roi-input-value">{apps}</span>
            </div>
            <input type="range" min="1" max="60" value={apps} onChange={e => setApps(Number(e.target.value))}
              className="prc-slider" style={{ '--pct': `${(apps / 60) * 100}%` }} />
          </div>
          <div className="prc-roi-input">
            <div className="prc-roi-input-label">
              <span>Current interview rate</span>
              <span className="prc-roi-input-value">{rate}%</span>
            </div>
            <input type="range" min="1" max="30" value={rate} onChange={e => setRate(Number(e.target.value))}
              className="prc-slider" style={{ '--pct': `${(rate / 30) * 100}%` }} />
          </div>
        </div>

        <div className="prc-roi-outputs">
          {[
            { Icon: TrendingUp, value: results.additionalInterviews, suffix: '', label: 'Est. additional interviews / year', color: '#059669', bg: '#ECFDF5' },
            { Icon: Clock, value: results.timeSavedHoursPerMonth, suffix: ' hrs', label: 'Time saved / month', color: '#1D4ED8', bg: '#EFF6FF' },
            { Icon: DollarSign, value: results.salaryPct, suffix: '%', label: 'Potential salary increase*', color: '#D97706', bg: '#FFFBEB' },
          ].map(out => (
            <div key={out.label} className="prc-roi-output">
              <div className="prc-roi-output-icon" style={{ background: out.bg, color: out.color }}><out.Icon size={18} /></div>
              <AnimatePresence mode="wait">
                <motion.div key={out.value} className="prc-roi-output-value" style={{ color: out.color }}
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.2 }}>
                  {out.value}{out.suffix}
                </motion.div>
              </AnimatePresence>
              <div className="prc-roi-output-label">{out.label}</div>
            </div>
          ))}
        </div>
        <div className="prc-roi-footnote">*Estimates for illustration only, based on general hiring trends from ATS-optimized applications. Actual results vary by industry, role, and market.</div>
      </div>
    </section>
  );
};

// ── Testimonials carousel ────────────────────────────────────────────────────
const TestimonialsCarousel = () => {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const total = TESTIMONIALS.length;

  const go = (dir) => {
    setDirection(dir);
    setIndex(i => (i + dir + total) % total);
  };

  useEffect(() => {
    const t = setInterval(() => {
      setDirection(1);
      setIndex(i => (i + 1) % total);
    }, 6000);
    return () => clearInterval(t);
  }, [total]);

  const t = TESTIMONIALS[index];

  return (
    <section className="prc-section">
      <SectionHeading title="Loved by job seekers everywhere" />
      <div className="prc-carousel">
        <button type="button" className="prc-carousel-nav prc-carousel-nav-left" onClick={() => go(-1)} aria-label="Previous testimonial"><ChevronLeft size={18} /></button>
        <div className="prc-carousel-track">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div key={index} className="prc-carousel-card"
              custom={direction}
              initial={{ opacity: 0, x: direction * 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -direction * 40 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}>
              <Quote size={26} className="prc-carousel-quote" />
              <StarRow />
              <p>{t.quote}</p>
              <div className="prc-carousel-person">
                <span className="prc-carousel-avatar" style={{ background: t.color }}>{t.initials}</span>
                <div>
                  <div className="prc-carousel-name">{t.name}</div>
                  <div className="prc-carousel-role">{t.role}</div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
        <button type="button" className="prc-carousel-nav prc-carousel-nav-right" onClick={() => go(1)} aria-label="Next testimonial"><ChevronRight size={18} /></button>
      </div>
      <div className="prc-carousel-dots">
        {TESTIMONIALS.map((_, i) => (
          <button key={i} type="button" className={`prc-carousel-dot${i === index ? ' active' : ''}`} onClick={() => { setDirection(i > index ? 1 : -1); setIndex(i); }} aria-label={`Go to testimonial ${i + 1}`} />
        ))}
      </div>
    </section>
  );
};

// ── FAQ ────────────────────────────────────────────────────────────────────────
const FAQSection = () => {
  const [openIdx, setOpenIdx] = useState(null);

  return (
    <section className="prc-section">
      <SectionHeading title="Frequently asked questions" />
      <div className="prc-faq-list">
        {FAQS.map(([q, a], i) => {
          const isOpen = openIdx === i;
          return (
            <div key={q} className={`prc-faq-item${isOpen ? ' open' : ''}`}>
              <button type="button" className="prc-faq-trigger" aria-expanded={isOpen} onClick={() => setOpenIdx(isOpen ? null : i)}>
                <span>{q}</span>
                <span className="prc-faq-icon">
                  <span className="bar h" />
                  <motion.span className="bar v" animate={{ rotate: isOpen ? 90 : 0 }} transition={{ duration: 0.25, ease: 'easeInOut' }} />
                </span>
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} style={{ overflow: 'hidden' }}>
                    <div className="prc-faq-answer">{a}</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </section>
  );
};

// ── Final CTA ──────────────────────────────────────────────────────────────────
const FINAL_CTA_TEMPLATE = TEMPLATES.find(t => t.id === 'harvard_standard') || TEMPLATES[0];
const FinalCTASection = ({ user, navigate }) => {
  const preview = buildShowcaseResume(FINAL_CTA_TEMPLATE);
  return (
    <section className="prc-cta-outer">
      <div className="prc-cta-card">
        <div className="prc-cta-mesh" />
        <div className="prc-cta-glow-1" />
        <div className="prc-cta-glow-2" />
        <div className="prc-cta-content">
          <div className="prc-cta-left">
            <h2>Your next job starts here.</h2>
            <p>Start free. Upgrade when you're ready.</p>
            {user ? (
              <button type="button" className="prc-btn-primary" onClick={() => navigate('/dashboard')}>Go to Dashboard <ArrowRight size={16} /></button>
            ) : (
              <button type="button" className="prc-btn-primary" onClick={() => navigate('/build/resume')}><Sparkles size={16} /> Start Building Free</button>
            )}
          </div>
          <div className="prc-cta-right">
            <motion.div className="prc-cta-preview-card" animate={{ y: [0, -10, 0] }} transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}>
              <div className="prc-cta-preview-chrome"><span /><span /><span /></div>
              <div className="prc-cta-preview-body">
                <div style={{ width: 480, transform: 'scale(0.42)', transformOrigin: 'top left', pointerEvents: 'none' }}>
                  <ResumePreview data={preview} id="pricing-final-cta" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ── Main page ────────────────────────────────────────────────────────────────
export const PricingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showAlert } = useDialog();

  usePageSEO({
    title: 'Pricing – MarsResume | Free AI Resume Builder',
    description: 'MarsResume is free forever. Upgrade to Pro for unlimited AI generation, ATS tailoring, and interview prep. No credit card required to start.',
    canonical: '/pricing',
  });

  const [billingCycle, setBillingCycle] = useState('annual');
  const pricingRef = useRef(null);
  const scrollToPricing = () => pricingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const handleUpgrade = async (cycle) => {
    if (!user) { navigate('/signup'); return; }
    try {
      await startCheckout({ billingCycle: cycle === 'annual' ? 'annual' : 'monthly' });
    } catch (e) {
      showAlert(e.message || 'Could not start checkout. Please try again.', { type: 'error', title: 'Checkout Error' });
    }
  };

  const isPro = user?.plan === 'pro';
  const isFreeUser = !!user && !isPro;
  const goToBuilder = () => navigate(user ? '/dashboard' : '/signup');
  const goTo = (path) => navigate(user ? (path === '/signup' ? '/dashboard' : path) : path);

  return (
    <div className="prc-page">
      <LandingStyles />
      <PricingPageStyles />
      <MarketingHeader />

      <HeroSection user={user} navigate={navigate} onSeePricing={scrollToPricing} />
      <TrustStatsSection />
      <div ref={pricingRef}>
        <PricingCardsSection
          user={user} isFreeUser={isFreeUser} isPro={isPro}
          billingCycle={billingCycle} setBillingCycle={setBillingCycle}
          goToBuilder={goToBuilder} handleUpgrade={handleUpgrade}
        />
      </div>
      <ComparisonSection />
      <WorkflowSection user={user} navigate={navigate} />
      <ROICalculatorSection />
      <TestimonialsCarousel />
      <FAQSection />
      <FinalCTASection user={user} navigate={navigate} />

      <MarketingFooter navigate={navigate} goTo={goTo} />
    </div>
  );
};

// ── Styles ─────────────────────────────────────────────────────────────────────
const PricingPageStyles = () => (
  <style>{`
    .prc-page { min-height: 100vh; background: #F9FAFB; font-family: 'Inter', sans-serif; color: #111827; overflow-x: hidden; }

    @keyframes prcHeroFloatA { 0%,100%{transform:rotate(0deg) translateY(0)} 50%{transform:rotate(0deg) translateY(-12px)} }
    @keyframes prcFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }

    /* Hero */
    .prc-hero { background: linear-gradient(160deg,#070E1A 0%,#0D1120 50%,#0A1420 100%); padding: 88px 48px 96px; position: relative; overflow: hidden; }
    .prc-hero-grid-bg { position: absolute; inset: 0; background-image: radial-gradient(rgba(255,255,255,0.06) 1px,transparent 1px); background-size: 28px 28px; pointer-events: none; }
    .prc-hero-glow { position: absolute; top: 10%; left: 15%; width: 700px; height: 420px; background: radial-gradient(ellipse, rgba(239,29,40,0.14) 0%, transparent 70%); pointer-events: none; }
    .prc-hero-inner { max-width: 1180px; margin: 0 auto; display: flex; align-items: center; gap: 56px; position: relative; }
    .prc-hero-left { flex: 0 0 46%; }
    .prc-hero-left h1 { margin: 18px 0 20px; font-size: 48px; line-height: 1.1; font-weight: 900; letter-spacing: -1.6px; color: white; }
    .prc-hero-left p { margin: 0 0 32px; font-size: 17px; line-height: 1.7; color: #94A3B8; max-width: 460px; }
    .prc-hero-ctas { display: flex; gap: 12px; flex-wrap: wrap; }
    .prc-hero-right { flex: 1; display: flex; align-items: center; justify-content: center; position: relative; min-height: 400px; }
    .prc-dash-wrap { position: relative; width: 420px; }

    .prc-dash-card { width: 420px; background: rgba(255,255,255,0.96); backdrop-filter: blur(18px); border-radius: 20px; box-shadow: 0 40px 90px rgba(0,0,0,0.45); border: 1px solid rgba(255,255,255,0.1); overflow: hidden; animation: prcHeroFloatA 6.5s ease-in-out infinite; position: relative; z-index: 2; }
    .prc-dash-chrome { background: #F1F5F9; padding: 11px 14px; display: flex; align-items: center; gap: 7px; border-bottom: 1px solid #E2E8F0; }
    .prc-dash-chrome span { width: 10px; height: 10px; border-radius: 50%; background: #CBD5E1; display: inline-block; }
    .prc-dash-chrome span:nth-child(1) { background: #FC5F5A; }
    .prc-dash-chrome span:nth-child(2) { background: #FDBA2F; }
    .prc-dash-chrome span:nth-child(3) { background: #28C840; }
    .prc-dash-url { flex: 1; background: white; border-radius: 6px; padding: 4px 10px; margin-left: 6px; font-size: 10px; color: #94A3B8; display: flex; align-items: center; gap: 5px; border: 1px solid #E2E8F0; }
    .prc-dash-body { padding: 16px; display: flex; flex-direction: column; gap: 10px; }
    .prc-dash-row { display: flex; align-items: center; gap: 12px; padding: 10px; border: 1px solid #F1F5F9; border-radius: 12px; background: #FAFBFF; }
    .prc-dash-row-icon { width: 34px; height: 34px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .prc-dash-row-text { flex: 1; min-width: 0; }
    .prc-dash-row-label { font-size: 12.5px; font-weight: 800; color: #111827; }
    .prc-dash-row-sub { font-size: 10.5px; color: #94A3B8; margin-top: 1px; }
    .prc-dash-row-value { font-size: 11.5px; font-weight: 800; padding: 4px 9px; border-radius: 7px; flex-shrink: 0; }

    .prc-float-badge { position: absolute; z-index: 5; display: flex; align-items: center; gap: 8px; background: rgba(255,255,255,0.92); backdrop-filter: blur(12px); border-radius: 12px; padding: 9px 14px 9px 9px; box-shadow: 0 16px 36px rgba(0,0,0,0.35); font-size: 12.5px; font-weight: 800; color: #111827; white-space: nowrap; animation: prcFloat 5.5s ease-in-out infinite; }
    .prc-float-badge-1 { top: -16px; right: -32px; animation-delay: 0.2s; }
    .prc-float-badge-2 { bottom: -16px; left: -32px; animation-delay: 0.6s; }
    .prc-float-icon { width: 26px; height: 26px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .prc-float-icon-green { background: linear-gradient(135deg,#D1FAE5,#A7F3D0); color: #059669; }
    .prc-float-icon-purple { background: linear-gradient(135deg,#F3E8FF,#E9D5FF); color: #7C3AED; }

    /* Buttons */
    .prc-btn-primary { background: #EF1D28; color: white; border: none; border-radius: 12px; padding: 14px 26px; font-weight: 700; font-size: 15px; cursor: pointer; font-family: inherit; display: inline-flex; align-items: center; gap: 8px; transition: all 0.2s; box-shadow: 0 6px 20px rgba(239,29,40,0.3); }
    .prc-btn-primary:hover { background: #D91620; transform: translateY(-2px); box-shadow: 0 10px 28px rgba(239,29,40,0.4); }
    .prc-btn-ghost-dark { background: rgba(255,255,255,0.06); border: 1.5px solid rgba(255,255,255,0.2); color: white; border-radius: 12px; padding: 14px 26px; font-weight: 700; font-size: 15px; cursor: pointer; font-family: inherit; transition: all 0.2s; }
    .prc-btn-ghost-dark:hover { background: rgba(255,255,255,0.12); border-color: rgba(255,255,255,0.35); transform: translateY(-2px); }

    /* Section heading */
    .prc-section { padding: 72px 48px; max-width: 1180px; margin: 0 auto; }
    .prc-section-tint { background: #FAFBFF; max-width: none; padding-left: 48px; padding-right: 48px; }
    .prc-section-tint > * { max-width: 1180px; margin-left: auto; margin-right: auto; }
    .prc-sec-heading { text-align: center; margin-bottom: 44px; }
    .prc-sec-heading h2 { margin: 0; font-size: 32px; font-weight: 900; color: #0F172A; letter-spacing: -0.8px; }
    .prc-sec-heading p { margin: 12px auto 0; color: #64748B; font-size: 15px; line-height: 1.65; max-width: 560px; }

    /* Trust stats */
    .prc-stats-grid { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 20px; }
    .prc-stat-card { background: white; border: 1px solid #E5E7EB; border-radius: 20px; padding: 30px 20px; text-align: center; box-shadow: 0 4px 20px rgba(15,23,42,0.05); transition: box-shadow 0.25s ease, border-color 0.25s ease; }
    .prc-stat-card:hover { box-shadow: 0 20px 48px rgba(15,23,42,0.1); border-color: #FBD3D5; }
    .prc-stat-icon { width: 48px; height: 48px; border-radius: 14px; background: #FFF1F2; color: #EF1D28; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; }
    .prc-stat-value { font-size: 32px; font-weight: 900; color: #0F172A; letter-spacing: -1px; margin-bottom: 6px; }
    .prc-stat-label { font-size: 13px; color: #64748B; font-weight: 600; }

    /* Pricing toggle */
    .prc-toggle-wrap { display: flex; justify-content: center; margin-bottom: 44px; }
    .prc-toggle { position: relative; display: inline-flex; gap: 4px; background: #F1F5F9; border: 1px solid #E5E7EB; border-radius: 999px; padding: 5px; }
    .prc-toggle-btn { position: relative; border: none; background: none; border-radius: 999px; padding: 10px 22px; font-size: 14px; font-weight: 700; cursor: pointer; font-family: inherit; overflow: hidden; }
    .prc-toggle-bg { position: absolute; inset: 0; background: #0F172A; border-radius: 999px; z-index: 0; box-shadow: 0 4px 14px rgba(15,23,42,0.25); }
    .prc-toggle-content { position: relative; z-index: 1; display: flex; align-items: center; gap: 8px; color: #64748B; }
    .prc-toggle-btn.active .prc-toggle-content { color: white; }
    .prc-toggle-save { background: #059669; color: white; border-radius: 999px; padding: 2px 8px; font-size: 10.5px; font-weight: 800; }

    /* Pricing cards */
    .prc-cards-grid { display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 24px; max-width: 780px; margin: 0 auto; }
    .prc-card { position: relative; background: white; border-radius: 24px; padding: 36px; border: 1.5px solid #E5E7EB; box-shadow: 0 4px 24px rgba(15,23,42,0.05); display: flex; flex-direction: column; }
    .prc-card-pro { background: linear-gradient(145deg,#0F1923 0%,#7B1C1C 55%,#C5161D 100%); border-color: rgba(239,29,40,0.25); box-shadow: 0 24px 70px rgba(127,29,29,0.35); overflow: hidden; }
    .prc-card-ribbon { position: absolute; top: 20px; right: -34px; transform: rotate(40deg); background: linear-gradient(135deg,#FB923C,#F59E0B); color: white; font-size: 11px; font-weight: 900; letter-spacing: 0.4px; padding: 5px 40px; box-shadow: 0 4px 12px rgba(0,0,0,0.2); }
    .prc-card-badge { display: inline-flex; align-items: center; gap: 6px; border-radius: 8px; padding: 4px 10px; font-size: 11px; font-weight: 700; margin-bottom: 12px; width: fit-content; }
    .prc-card-badge-neutral { background: #F1F5F9; color: #475569; }
    .prc-card-badge-pro { background: rgba(255,255,255,0.12); color: rgba(255,255,255,0.8); }
    .prc-card-name { font-size: 24px; font-weight: 900; margin-bottom: 4px; color: #0F172A; }
    .prc-card-pro .prc-card-name { color: white; }
    .prc-card-desc { font-size: 14px; color: #64748B; margin-bottom: 24px; line-height: 1.5; }
    .prc-card-pro .prc-card-desc { color: rgba(255,255,255,0.65); }
    .prc-card-price { display: flex; align-items: flex-end; gap: 4px; margin-bottom: 6px; }
    .prc-card-price-amount { font-size: 48px; font-weight: 950; color: #0F172A; line-height: 1; }
    .prc-card-pro .prc-card-price-amount { color: white; }
    .prc-card-price-period { font-size: 15px; color: #94A3B8; padding-bottom: 8px; }
    .prc-card-pro .prc-card-price-period { color: rgba(255,255,255,0.5); }
    .prc-card-price-sub { font-size: 13px; color: #94A3B8; margin-bottom: 24px; }
    .prc-card-pro .prc-card-price-sub { color: rgba(255,255,255,0.5); }
    .prc-card-current { background: #F3F4F6; color: #6B7280; border-radius: 12px; padding: 14px; text-align: center; font-weight: 700; font-size: 14px; margin-bottom: 24px; }
    .prc-card-current-pro { background: rgba(255,255,255,0.15); color: white; }
    .prc-btn-dark-full { width: 100%; border: none; border-radius: 12px; padding: 15px; font-weight: 800; font-size: 15px; cursor: pointer; font-family: inherit; display: flex; align-items: center; justify-content: center; gap: 8px; background: linear-gradient(135deg,#0F172A,#1E293B); color: white; margin-bottom: 24px; transition: all 0.2s; }
    .prc-btn-dark-full:hover { transform: translateY(-1px); opacity: 0.92; }
    .prc-btn-white-full { width: 100%; border: none; border-radius: 12px; padding: 15px; font-weight: 900; font-size: 15px; cursor: pointer; font-family: inherit; display: flex; align-items: center; justify-content: center; gap: 8px; background: white; color: #EF1D28; margin-bottom: 24px; transition: all 0.2s; }
    .prc-btn-white-full:hover { transform: translateY(-1px); opacity: 0.92; }
    .prc-card-divider { height: 1px; background: #F1F5F9; margin-bottom: 20px; }
    .prc-card-divider-pro { background: rgba(255,255,255,0.12); }
    .prc-card-features { display: flex; flex-direction: column; gap: 13px; flex: 1; }
    .prc-card-feature { display: flex; align-items: flex-start; gap: 10px; color: #374151; font-size: 14px; line-height: 1.5; }
    .prc-card-feature-pro { color: rgba(255,255,255,0.9); }
    .prc-card-feature-icon { width: 20px; height: 20px; border-radius: 6px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 1px; }
    .prc-card-feature-icon-neutral { background: #F1F5F9; color: #64748B; }
    .prc-card-feature-icon-pro { background: rgba(251,146,60,0.25); color: #FB923C; }

    .prc-trust-row { display: flex; justify-content: center; gap: 32px; flex-wrap: wrap; margin-top: 44px; }
    .prc-trust-item { display: flex; align-items: center; gap: 7px; font-size: 13px; color: #64748B; }
    .prc-trust-item svg { color: #EF1D28; }

    /* Comparison */
    .prc-compare-grid { display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 24px; max-width: 980px; margin: 0 auto; }
    .prc-compare-col { background: white; border-radius: 20px; border: 1.5px solid #E5E7EB; overflow: hidden; }
    .prc-compare-col-with { border-color: #BBF7D0; box-shadow: 0 12px 34px rgba(5,150,105,0.08); }
    .prc-compare-head { padding: 18px 24px; font-size: 15px; font-weight: 800; border-bottom: 1px solid #F1F5F9; }
    .prc-compare-head-without { color: #94A3B8; background: #FAFAFA; }
    .prc-compare-head-with { color: #059669; background: #F0FDF4; }
    .prc-compare-row { display: flex; align-items: flex-start; gap: 12px; padding: 16px 24px; font-size: 14px; color: #374151; line-height: 1.5; border-top: 1px solid #F8FAFC; }
    .prc-compare-row:first-of-type { border-top: none; }
    .prc-compare-icon { width: 22px; height: 22px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 1px; }
    .prc-compare-icon-without { background: #FEF2F2; color: #EF4444; }
    .prc-compare-icon-with { background: #ECFDF5; color: #059669; }

    /* Workflow */
    .prc-workflow-outer { background: white; }
    .prc-workflow-grid { display: grid; grid-template-columns: 1fr 32px 1fr 32px 1fr 32px 1fr; align-items: stretch; margin-bottom: 48px; }
    .prc-workflow-card { position: relative; background: white; border: 1px solid #E5E7EB; border-radius: 20px; padding: 28px 22px 24px; box-shadow: 0 4px 24px rgba(15,23,42,0.05); transition: box-shadow 0.25s ease, border-color 0.25s ease; }
    .prc-workflow-card:hover { box-shadow: 0 20px 48px rgba(15,23,42,0.1); border-color: #FBD3D5; }
    .prc-workflow-step-num { position: absolute; top: 22px; right: 22px; font-size: 12px; font-weight: 800; color: #E2E8F0; letter-spacing: 0.5px; }
    .prc-workflow-icon { width: 48px; height: 48px; border-radius: 14px; background: linear-gradient(135deg,#EF1D28,#FF6B6B); color: white; display: flex; align-items: center; justify-content: center; margin-bottom: 18px; box-shadow: 0 8px 20px rgba(239,29,40,0.28); }
    .prc-workflow-title { font-size: 15px; font-weight: 800; color: #0F172A; margin-bottom: 8px; line-height: 1.3; }
    .prc-workflow-desc { margin: 0; color: #64748B; font-size: 12.5px; line-height: 1.6; }
    .prc-workflow-arrow { position: relative; display: flex; align-items: center; justify-content: center; }
    .prc-workflow-arrow-line { width: 100%; height: 2px; background: linear-gradient(90deg,#FED7D7,#EF1D28); border-radius: 2px; }
    .prc-workflow-arrow-chip { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); width: 26px; height: 26px; border-radius: 50%; background: white; border: 1.5px solid #FBD3D5; color: #EF1D28; display: flex; align-items: center; justify-content: center; }
    .prc-workflow-arrow-wrap { display: none; }

    .prc-workflow-summary { max-width: 780px; margin: 0 auto; background: linear-gradient(145deg,#0F1923 0%,#7B1C1C 55%,#C5161D 100%); border-radius: 24px; padding: 44px 40px; text-align: center; box-shadow: 0 24px 70px rgba(127,29,29,0.3); }
    .prc-workflow-summary-emoji { font-size: 28px; margin-bottom: 14px; }
    .prc-workflow-summary-text { margin-bottom: 26px; }
    .prc-workflow-summary-text strong { display: block; font-size: 22px; font-weight: 900; color: white; margin-bottom: 8px; letter-spacing: -0.4px; }
    .prc-workflow-summary-text span { display: block; font-size: 14.5px; color: rgba(255,255,255,0.7); line-height: 1.6; max-width: 480px; margin: 0 auto; }
    .prc-workflow-cta { border: none; border-radius: 12px; padding: 15px 30px; font-weight: 900; font-size: 15px; cursor: pointer; font-family: inherit; display: inline-flex; align-items: center; gap: 8px; background: white; color: #EF1D28; transition: all 0.2s; }
    .prc-workflow-cta:hover { transform: translateY(-2px); box-shadow: 0 10px 28px rgba(0,0,0,0.25); }
    .prc-workflow-summary-sub { margin-top: 14px; font-size: 12px; color: rgba(255,255,255,0.5); }

    /* ROI calculator */
    .prc-roi-card { max-width: 900px; margin: 0 auto; background: white; border: 1.5px solid #E5E7EB; border-radius: 24px; padding: 40px; box-shadow: 0 12px 40px rgba(15,23,42,0.06); }
    .prc-roi-inputs { display: flex; flex-direction: column; gap: 28px; margin-bottom: 36px; }
    .prc-roi-input-label { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; font-size: 14px; font-weight: 700; color: #374151; }
    .prc-roi-input-value { color: #EF1D28; font-weight: 900; font-size: 15px; }
    .prc-slider { width: 100%; -webkit-appearance: none; appearance: none; height: 6px; border-radius: 999px; background: linear-gradient(to right, #EF1D28 0%, #EF1D28 var(--pct,50%), #E5E7EB var(--pct,50%), #E5E7EB 100%); outline: none; cursor: pointer; }
    .prc-slider::-webkit-slider-thumb { -webkit-appearance: none; width: 20px; height: 20px; border-radius: 50%; background: white; border: 3px solid #EF1D28; box-shadow: 0 2px 8px rgba(0,0,0,0.18); cursor: pointer; }
    .prc-slider::-moz-range-thumb { width: 20px; height: 20px; border-radius: 50%; background: white; border: 3px solid #EF1D28; box-shadow: 0 2px 8px rgba(0,0,0,0.18); cursor: pointer; }
    .prc-roi-outputs { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 18px; padding-top: 28px; border-top: 1px solid #F1F5F9; }
    .prc-roi-output { text-align: center; }
    .prc-roi-output-icon { width: 40px; height: 40px; border-radius: 12px; display: flex; align-items: center; justify-content: center; margin: 0 auto 12px; }
    .prc-roi-output-value { font-size: 30px; font-weight: 900; letter-spacing: -0.5px; margin-bottom: 4px; }
    .prc-roi-output-label { font-size: 12.5px; color: #64748B; font-weight: 600; }
    .prc-roi-footnote { margin-top: 24px; font-size: 11.5px; color: #94A3B8; text-align: center; line-height: 1.6; }

    /* Testimonials carousel */
    .prc-carousel { display: flex; align-items: center; justify-content: center; gap: 16px; max-width: 720px; margin: 0 auto; }
    .prc-carousel-track { flex: 1; min-height: 260px; display: flex; align-items: center; overflow: hidden; }
    .prc-carousel-card { width: 100%; background: white; border: 1px solid #E5E7EB; border-radius: 22px; padding: 36px; box-shadow: 0 12px 40px rgba(15,23,42,0.06); }
    .prc-carousel-quote { color: #FECACA; margin-bottom: 12px; }
    .prc-carousel-card p { margin: 14px 0 22px; color: #374151; font-size: 16px; line-height: 1.75; }
    .prc-carousel-person { display: flex; align-items: center; gap: 12px; }
    .prc-carousel-avatar { width: 40px; height: 40px; border-radius: 50%; color: white; font-size: 13px; font-weight: 800; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .prc-carousel-name { font-size: 14px; font-weight: 800; color: #111827; }
    .prc-carousel-role { font-size: 12.5px; color: #94A3B8; }
    .prc-carousel-nav { flex-shrink: 0; width: 42px; height: 42px; border-radius: 50%; background: white; border: 1.5px solid #E2E8F0; box-shadow: 0 4px 16px rgba(0,0,0,0.08); cursor: pointer; display: flex; align-items: center; justify-content: center; color: #374151; transition: all 0.18s; }
    .prc-carousel-nav:hover { background: #0F172A; border-color: #0F172A; color: white; }
    .prc-carousel-dots { display: flex; justify-content: center; gap: 8px; margin-top: 24px; }
    .prc-carousel-dot { width: 8px; height: 8px; border-radius: 50%; background: #E2E8F0; border: none; cursor: pointer; padding: 0; transition: all 0.2s; }
    .prc-carousel-dot.active { background: #EF1D28; width: 22px; border-radius: 4px; }

    /* FAQ */
    .prc-faq-list { display: flex; flex-direction: column; gap: 12px; max-width: 780px; margin: 0 auto; }
    .prc-faq-item { background: white; border: 1.5px solid #E5E7EB; border-radius: 16px; overflow: hidden; transition: border-color 0.25s ease, box-shadow 0.25s ease; }
    .prc-faq-item.open { border-color: #FECACA; box-shadow: 0 10px 30px rgba(239,29,40,0.08); }
    .prc-faq-trigger { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 20px 24px; background: transparent; border: none; cursor: pointer; text-align: left; font-family: inherit; }
    .prc-faq-trigger span:first-child { font-size: 15.5px; font-weight: 700; color: #111827; line-height: 1.4; }
    .prc-faq-icon { position: relative; width: 30px; height: 30px; border-radius: 9px; background: #F9FAFB; border: 1px solid #E5E7EB; flex-shrink: 0; transition: background 0.25s ease, border-color 0.25s ease; }
    .prc-faq-item.open .prc-faq-icon { background: #FFF1F2; border-color: #FECDD3; }
    .prc-faq-icon .bar { position: absolute; top: 50%; left: 50%; background: #6B7280; border-radius: 2px; }
    .prc-faq-icon .bar.h { width: 12px; height: 2px; margin: -1px 0 0 -6px; }
    .prc-faq-icon .bar.v { width: 2px; height: 12px; margin: -6px 0 0 -1px; }
    .prc-faq-item.open .prc-faq-icon .bar { background: #EF1D28; }
    .prc-faq-answer { padding: 0 24px 22px; font-size: 14.5px; color: #6B7280; line-height: 1.8; }

    /* Final CTA */
    .prc-cta-outer { padding: 20px 48px 96px; }
    .prc-cta-card { max-width: 1100px; margin: 0 auto; background: linear-gradient(150deg, #060D1A 0%, #0D1120 50%, #0A1428 100%); border-radius: 28px; overflow: hidden; position: relative; padding: 72px 56px; border: 1px solid #1A2A3E; }
    .prc-cta-mesh { position: absolute; inset: 0; background-image: radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px); background-size: 28px 28px; pointer-events: none; }
    .prc-cta-glow-1 { position: absolute; top: -120px; right: -80px; width: 500px; height: 420px; background: radial-gradient(circle, rgba(239,29,40,0.16) 0%, transparent 60%); pointer-events: none; }
    .prc-cta-glow-2 { position: absolute; bottom: -80px; left: -60px; width: 340px; height: 340px; background: radial-gradient(circle, rgba(29,78,216,0.1) 0%, transparent 65%); pointer-events: none; }
    .prc-cta-content { position: relative; display: flex; align-items: center; justify-content: space-between; gap: 48px; flex-wrap: wrap; }
    .prc-cta-left { flex: 0 0 auto; }
    .prc-cta-left h2 { margin: 0 0 12px; font-size: 38px; font-weight: 900; color: white; letter-spacing: -1px; line-height: 1.15; }
    .prc-cta-left p { margin: 0 0 28px; color: #94A3B8; font-size: 16px; }
    .prc-cta-right { flex: 1; min-width: 280px; display: flex; justify-content: center; }
    .prc-cta-preview-card { width: 320px; background: white; border-radius: 16px; box-shadow: 0 40px 90px rgba(0,0,0,0.5); overflow: hidden; }
    .prc-cta-preview-chrome { background: #F1F5F9; padding: 9px 12px; display: flex; gap: 5px; }
    .prc-cta-preview-chrome span { width: 8px; height: 8px; border-radius: 50%; background: #CBD5E1; display: inline-block; }
    .prc-cta-preview-body { height: 220px; overflow: hidden; position: relative; background: white; }

    /* Responsive */
    @media (max-width: 1100px) {
      .prc-stats-grid { grid-template-columns: repeat(2, minmax(0,1fr)); }
    }
    @media (max-width: 900px) {
      .prc-hero { padding: 64px 24px 80px; }
      .prc-hero-inner { flex-direction: column; gap: 48px; text-align: center; }
      .prc-hero-left { flex: none; }
      .prc-hero-left p { margin-left: auto; margin-right: auto; }
      .prc-hero-ctas { justify-content: center; }
      .prc-hero-left h1 { font-size: 38px; }
      .prc-section { padding: 56px 24px; }
      .prc-section-tint { padding-left: 24px; padding-right: 24px; }
      .prc-cards-grid { grid-template-columns: 1fr; max-width: 420px; }
      .prc-compare-grid { grid-template-columns: 1fr; }
      .prc-roi-outputs { grid-template-columns: 1fr; gap: 24px; }
      .prc-cta-content { flex-direction: column; text-align: center; }
      .prc-cta-left { text-align: center; }
      .prc-workflow-grid { grid-template-columns: 1fr; gap: 0; max-width: 420px; margin-left: auto; margin-right: auto; }
      .prc-workflow-arrow { min-height: 56px; }
      .prc-workflow-arrow-line { width: 2px; height: 32px; background: linear-gradient(180deg,#FED7D7,#EF1D28); }
      .prc-workflow-arrow-wrap { display: flex; }
      .prc-workflow-arrow-chip svg { transform: rotate(90deg); }
      .prc-workflow-summary { padding: 36px 28px; }
    }
    @media (max-width: 640px) {
      .prc-stats-grid { grid-template-columns: 1fr 1fr; }
      .prc-hero-left h1 { font-size: 30px; letter-spacing: -1px; }
      .prc-hero-right { display: none; }
      .prc-cta-card { padding: 48px 24px; }
      .prc-cta-left h2 { font-size: 27px; }
      .prc-cta-right { display: none; }
      .prc-carousel { flex-direction: column; }
      .prc-carousel-nav { display: none; }
    }
  `}</style>
);
